'use client'

import { ChoiceChip } from '@/components/molecules/choice-chip.client'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * When the booking starts — as buttons, not a dropdown.
 *
 * A room publishes eight to twelve legal starts on a given day. That fits on
 * screen, and a dropdown hid it: two clicks and a scroll to see a list that
 * could have been the control itself, with no way to tell at a glance that the
 * afternoon was gone until you opened it. `startSlots` already refuses anything
 * the whole booking will not fit inside, so every button drawn here is a start
 * the server will accept.
 *
 * The end time is shown on the pressed button rather than in a sentence
 * underneath, because "10:00" alone is not the question a person is answering —
 * they are choosing a block, and the block has two ends.
 */
export function HourPicker({
  slots,
  value,
  minutes,
  onChange,
  hasDay,
}: {
  /** Legal starts for this day and length — see `startSlots`. */
  slots: readonly string[]
  value: string
  /** How long the booking runs, so a pressed start can say where it ends. */
  minutes: number
  onChange: (time: string) => void
  /** Whether a day is chosen at all. Nothing can be offered before one is. */
  hasDay: boolean
}) {
  if (slots.length === 0) {
    return (
      <p
        className={cn(
          MICRO,
          'rounded-2xl border border-dashed border-white/15 px-4 py-3.5 text-white/55',
        )}
      >
        {hasDay
          ? 'Tego dnia studio jest zamknięte albo nie zmieści już tylu godzin — wybierz inny dzień.'
          : 'Najpierw wybierz dzień, a pokażemy wolne godziny.'}
      </p>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {slots.map((slot) => (
        <ChoiceChip
          key={slot}
          pressed={slot === value}
          onClick={() => onChange(slot)}
          className="px-3.5 tabular-nums"
          {...(slot === value ? { note: `→ ${endOf(slot, minutes)}` } : {})}
        >
          {slot}
        </ChoiceChip>
      ))}
    </div>
  )
}

/** `HH:MM` plus a length, wrapped past midnight the way a clock is read. */
function endOf(start: string, minutes: number): string {
  const [h, m] = start.split(':').map(Number)
  const total = ((h ?? 0) * 60 + (m ?? 0) + minutes) % (24 * 60)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
